import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { useGlobal } from "../../../contexts/globalContext";
import { useJanusContext } from "../../../contexts/janus/JanusContext";
import Janus from "../../../utils/janus";
import { isChrome } from "../../../utils/userAgent";
import useDidMountEffect from "../../useDidMountEffect";
import usePreviousValue from "../../usePreviousValue";

import { SCREEN_SWITCH_STEP_COLLECTION } from "./constants";
import { useScreenSwitchSteps } from "./useScreenSwitchSteps";

export const useScreenFlowController = () => {
  const {
    SCREEN_STATE,
    SET_SCREEN_STATE,
    MEDIA_TRACKS,
    SCREEN_SWITCH_STEP,
    SET_SCREEN_SWITCH_STEP,
    SCREEN_SWITCH_DEBUG_MODE,
    SCREEN_REF,
    MAIN_SCREEN_STREAM_LOADED,

    // STREAM CONTAINERS
    SCREEN_STREAM_MAIN_REF,
    SCREEN_STREAM_TEMP_REF,

    // MIDIA ELEMENTS IN USE
    VIDEO_MAIN_HIDDEN,
    VIDEO_MAIN,
    VIDEO_MINI,
    VIDEO_TEMP_HIDDEN,
    VIDEO_TEMP,

    // CONTENT ELEMENTS
    SPAN_SIZE_TEMP,
    SPAN_SIZE_MAIN,
  } = useGlobal();

  const { janusInstance, StreamingPlugin, MEDIA_TRACK_IDS, oldScreensArray } = useJanusContext();

  const {
    handleBeginPutTargetMiniIntoTemp,
    handleXxx,
    handleTempVideoResizeShowTempVideo,
    handleSwitchToBaseScreenStream,
    handleMainVideoResizeSwitchToTargetScreenStream,
    handleMainVideoResizeShowMainVideo,
    handleEndPutBaseMiniIntoTemp,
    handleFinishLoadingMainStream,
    handleInsertAnAudioTrack,
    handlePlayAnAudioTrack,
  } = useScreenSwitchSteps();

  const screensCopyToUseInsideSocketRef = useRef(null);

  const [timestamp, setTimestamp] = useState(0);

  const previousStep = usePreviousValue(SCREEN_SWITCH_STEP.step);
  const prevScreenId = usePreviousValue(SCREEN_STATE.currentScreen.id);

  const handleFinalStepSwitch = () => {
    if (MAIN_SCREEN_STREAM_LOADED && SCREEN_SWITCH_STEP.step === SCREEN_SWITCH_STEP_COLLECTION.END_PUT_BASE_MINI_INTO_TEMP) {
      console.log("!!!! SWITCH TO FINAL STEP SINCE SCREEN IS LOADED AFTER 6th STEP PASSED");
      SET_SCREEN_SWITCH_STEP({
        step: SCREEN_SWITCH_DEBUG_MODE
          ? SCREEN_SWITCH_STEP_COLLECTION.FINISH_LOADING_MAIN_STREAM__DEBUG
          : SCREEN_SWITCH_STEP_COLLECTION.FINISH_LOADING_MAIN_STREAM,
      });
    }
  };

  useDidMountEffect(() => {
    const baseScreen = oldScreensArray.find((x) => x.id === -1);

    if (previousStep === SCREEN_SWITCH_STEP.step) {
      console.log("!!!!!prevStep: ", previousStep, MAIN_SCREEN_STREAM_LOADED);
      handleFinalStepSwitch();
      return;
    }

    switch (SCREEN_SWITCH_STEP.step) {
      case SCREEN_SWITCH_STEP_COLLECTION.BEGIN_PUT_TARGET_MINI_INTO_TEMP:
        handleBeginPutTargetMiniIntoTemp(baseScreen);
        break;

      case SCREEN_SWITCH_STEP_COLLECTION.XXX:
        handleXxx();
        break;

      case SCREEN_SWITCH_STEP_COLLECTION.TEMP_VIDEO_RESIZE_SHOW_TEMP_VIDEO:
        handleTempVideoResizeShowTempVideo();
        break;

      case SCREEN_SWITCH_STEP_COLLECTION.SWITCH_TO_BASE_SCREEN_STREAM:
        handleSwitchToBaseScreenStream(baseScreen);
        break;

      case SCREEN_SWITCH_STEP_COLLECTION.MAIN_VIDEO_RESIZE_SWITCH_TO_TARGET_SCREEN_STREAM:
        handleMainVideoResizeSwitchToTargetScreenStream();

        break;

      case SCREEN_SWITCH_STEP_COLLECTION.MAIN_VIDEO_RESIZE_SHOW_MAIN_VIDEO:
        handleMainVideoResizeShowMainVideo();
        break;

      case SCREEN_SWITCH_STEP_COLLECTION.END_PUT_BASE_MINI_INTO_TEMP:
        handleEndPutBaseMiniIntoTemp(baseScreen);
        break;

      case SCREEN_SWITCH_STEP_COLLECTION.FINISH_LOADING_MAIN_STREAM:
        handleFinishLoadingMainStream();
        break;

      case SCREEN_SWITCH_STEP_COLLECTION.INSERT_AN_AUDIO_TRACK:
        handleInsertAnAudioTrack();

        break;

      case SCREEN_SWITCH_STEP_COLLECTION.PLAY_AN_AUDIO_TRACK:
        handlePlayAnAudioTrack();
        break;

      default:
        console.error("Unexpected step in screen-switch flow");
    }
  }, [SCREEN_SWITCH_STEP, MAIN_SCREEN_STREAM_LOADED]);

  useDidMountEffect(() => {
    if (
      MEDIA_TRACK_IDS.length === 0 ||
      Object.keys(MEDIA_TRACKS).length !== MEDIA_TRACK_IDS.length ||
      SCREEN_STATE.currentScreen.state !== "switching"
    ) {
      return;
    }
    console.log("SET_SCREEN_SWITCH_STEP BEGIN_PUT_TARGET_MINI_INTO_TEMP");
    SET_SCREEN_SWITCH_STEP({
      step: SCREEN_SWITCH_DEBUG_MODE
        ? SCREEN_SWITCH_STEP_COLLECTION.BEGIN_PUT_TARGET_MINI_INTO_TEMP__DEBUG
        : SCREEN_SWITCH_STEP_COLLECTION.BEGIN_PUT_TARGET_MINI_INTO_TEMP,
    });
  }, [SCREEN_STATE.currentScreen.id]);

  // init DOM elements
  useLayoutEffect(() => {
    if (janusInstance) setTimestamp(+new Date());
  }, []);

  useDidMountEffect(() => {
    Janus.attachMediaStream(VIDEO_MAIN.current, SCREEN_STREAM_MAIN_REF.current);
    Janus.attachMediaStream(VIDEO_TEMP.current, SCREEN_STREAM_TEMP_REF.current);
  }, [timestamp]);

  useDidMountEffect(() => {
    screensCopyToUseInsideSocketRef.current = oldScreensArray;
  }, [oldScreensArray]);

  const handleTempVideoResize = (e) => {
    if (prevScreenId !== SCREEN_STATE.currentScreen.id) {
      SPAN_SIZE_TEMP.current.innerText = `${e.target.videoWidth}x${e.target.videoHeight}`;
      if (StreamingPlugin.current === null || SCREEN_STATE.currentScreen.state !== "switching") return;

      SET_SCREEN_SWITCH_STEP({
        step: SCREEN_SWITCH_DEBUG_MODE
          ? SCREEN_SWITCH_STEP_COLLECTION.TEMP_VIDEO_RESIZE_SHOW_TEMP_VIDEO__DEBUG
          : SCREEN_SWITCH_STEP_COLLECTION.TEMP_VIDEO_RESIZE_SHOW_TEMP_VIDEO,
        payload: e.target,
      });
    }
  };

  const handleMainVideoResize = useCallback(
    (e) => {
      console.log(prevScreenId, SCREEN_STATE.currentScreen.id);
      if (prevScreenId !== SCREEN_STATE.currentScreen.id) {
        SPAN_SIZE_MAIN.current.innerText = `${e.target.videoWidth}x${e.target.videoHeight}`;

        if (StreamingPlugin.current === null || SCREEN_STATE.currentScreen.state !== "switching") return;

        const baseScreen = oldScreensArray.find((x) => x.id === -1);

        const screen = oldScreensArray.find((x) => x.id === SCREEN_STATE.currentScreen.id);
        console.log("!!!!!!!!!!!!!!", oldScreensArray, SCREEN_STATE.currentScreen.id);
        console.log(e.target.videoWidth, baseScreen.res[0], e.target.videoWidth === baseScreen.res[0]);
        if (e.target.videoWidth === baseScreen.res[0]) {
          SET_SCREEN_SWITCH_STEP({
            step: SCREEN_SWITCH_DEBUG_MODE
              ? SCREEN_SWITCH_STEP_COLLECTION.MAIN_VIDEO_RESIZE_SWITCH_TO_TARGET_SCREEN_STREAM__DEBUG
              : SCREEN_SWITCH_STEP_COLLECTION.MAIN_VIDEO_RESIZE_SWITCH_TO_TARGET_SCREEN_STREAM,
            payload: screen.stream_id,
          });
        }

        console.log("e.target.videoWidth, screen.io_m_res[0]: ", e.target.videoWidth, screen.io_m_res[0]);
        console.log("e.target.videoWidth, screen.io_m_res[1]: ", e.target.videoWidth, screen.io_m_res[1]);
        console.log("e.target.videoWidth, baseScreen.res[0]: ", e.target.videoWidth, baseScreen.res[0]);

        if (e.target.videoWidth !== baseScreen.res[0]) {
          SET_SCREEN_SWITCH_STEP({
            step: SCREEN_SWITCH_DEBUG_MODE
              ? SCREEN_SWITCH_STEP_COLLECTION.MAIN_VIDEO_RESIZE_SHOW_MAIN_VIDEO__DEBUG
              : SCREEN_SWITCH_STEP_COLLECTION.MAIN_VIDEO_RESIZE_SHOW_MAIN_VIDEO,
          });
        }
      }
    },
    [SCREEN_STATE.currentScreen],
  );

  const handleFullScreenChange = () => {
    VIDEO_MAIN.current.removeAttribute("width");
    VIDEO_TEMP.current.removeAttribute("width");
    VIDEO_MINI.current.removeAttribute("width");

    const screen = oldScreensArray?.find((screenItem) => screenItem.id === SCREEN_STATE.currentScreen.id);
    VIDEO_TEMP.current.setAttribute("width", VIDEO_MAIN.current.offsetWidth);
    if (screen) VIDEO_MAIN.current.style.transform = document.fullscreenElement ? `scale(${screen.scale})` : "scale(1)";
  };

  useEffect(() => {
    document.addEventListener("fullscreenchange", handleFullScreenChange, 0);

    return () => document.removeEventListener("fullscreenchange", handleFullScreenChange, 0);
  }, []);

  useEffect(() => {
    if (SCREEN_STATE.currentScreen.id !== null) {
      if (VIDEO_MAIN.current && VIDEO_TEMP.current) {
        console.log("VIDEO_MAIN.current && VIDEO_TEMP.current");
        VIDEO_MAIN_HIDDEN.current.addEventListener("resize", handleMainVideoResize, 0);
        VIDEO_TEMP_HIDDEN.current.addEventListener("resize", handleTempVideoResize, 0);
      }

      return () => {
        if (VIDEO_MAIN_HIDDEN.current && VIDEO_TEMP_HIDDEN.current) {
          VIDEO_MAIN_HIDDEN.current.removeEventListener("resize", handleMainVideoResize, 0);
          VIDEO_TEMP_HIDDEN.current.removeEventListener("resize", handleTempVideoResize, 0);
        }
      };
    }
  }, [SCREEN_STATE.currentScreen]);

  useEffect(() => {
    const canPlayEventListener = () => {
      console.log("mini canplay");
      VIDEO_MINI.current.removeEventListener("canplay", canPlayEventListener);
    };

    const { id, state: currentScreenState } = SCREEN_STATE.currentScreen;

    if (SCREEN_REF.current.tracks.length && id) {
      console.log("canPlayEventListener: ", SCREEN_REF.current.tracks, id);
      // const screen = SCREEN_STATE.screens.find((x) => x.id === id);
      // const [w, h] = screen.io_m_res;

      runConditionalStyles(currentScreenState /* w, h */);

      VIDEO_MINI.current?.addEventListener("canplay", canPlayEventListener);
    }
  }, [SCREEN_STATE.currentScreen.id, SCREEN_STATE.currentScreen.state]);

  function runConditionalStyles(currentScreenState /* w, h */) {
    if (!document.fullscreenElement) {
      if (SCREEN_SWITCH_STEP.step === "WAITING") {
        VIDEO_MINI.current && (VIDEO_MINI.current.style.opacity = 0);
      } else {
        VIDEO_MINI.current && VIDEO_MINI.current.removeAttribute("style");
      }

      if (currentScreenState === "switching") applySwitchingStyles();
    } else {
      VIDEO_MINI.current.style.zIndex = 2;
    }
  }

  function applySwitchingStyles() {
    VIDEO_MINI.current && (VIDEO_MINI.current.style.zIndex = 2);
    VIDEO_MINI.current && (VIDEO_MINI.current.style.opacity = 1);
  }

  // Prepare Temporary Screen
  useDidMountEffect(() => {
    if (MEDIA_TRACK_IDS) {
      if (
        MEDIA_TRACK_IDS?.length === 0 ||
        Object.keys(MEDIA_TRACKS).length !== MEDIA_TRACK_IDS.length ||
        SCREEN_STATE.currentScreen.state !== "initializing"
      ) {
        return;
      }

      if (isChrome) MEDIA_TRACK_IDS.forEach((t) => SCREEN_STREAM_TEMP_REF.current.addTrack(MEDIA_TRACKS[t]));

      SET_SCREEN_STATE((p) => ({
        ...p,
        currentScreen: {
          ...p.currentScreen,
          state: "playing",
        },
      }));
    }
  }, [MEDIA_TRACKS, MEDIA_TRACK_IDS]);

  return {
    handleFinalStepSwitch,
  };
};
